import type { UniclientFrameType } from '@lemonade-hq/uniclient-chat';
import type { ResponsiveValues } from '@lemonade-hq/uniclient-theme';
import { Flex, getCssVarName, Layout, useMobileKeyboardHeight, useResponsiveValue } from '@lemonade-hq/uniclient-theme';
import { setElementVars } from '@vanilla-extract/dynamic';
import { clsx } from 'clsx';
import { motion, useMotionTemplate, useMotionValueEvent, useScroll, useTransform } from 'motion/react';
import type { PropsWithChildren, RefObject } from 'react';
import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { MOBILE_HEADER_SAFE_AREA } from './layout';
import { hasVisibleHistoryItemVar } from './Wizard.css';
import { HEADER_HEIGHT, HEADER_MARGIN } from './WizardHeader.css';
import * as styles from './WizardMain.css';

const hasVisibleHistoryItemVarName = getCssVarName(hasVisibleHistoryItemVar);

export const HEADER_WITH_MARGIN = {
  sm: HEADER_HEIGHT.sm + HEADER_MARGIN.sm + MOBILE_HEADER_SAFE_AREA,
  lg: HEADER_HEIGHT.lg + HEADER_MARGIN.lg,
} as const satisfies ResponsiveValues<number>;

interface WizardMainProps {
  readonly frameType?: UniclientFrameType;
  readonly scrollTrackingTargetRef?: RefObject<HTMLDivElement>;
  readonly containerRef?: RefObject<HTMLDivElement>;
  readonly currentStepIndex?: number;
  readonly historyLength?: number;
}

export const WizardMain = forwardRef<HTMLDivElement, PropsWithChildren<WizardMainProps>>(
  ({ children, scrollTrackingTargetRef, frameType, containerRef, currentStepIndex, historyLength = 0 }, ref) => {
    const headerHeight = useResponsiveValue(HEADER_WITH_MARGIN);
    const { scrollYProgress: scrollYContainerProgress } = useScroll({
      container: ref as RefObject<HTMLDivElement>,
    });

    const targetRef = useRef<HTMLDivElement | null>(null);

    // remove this once css floor() function is supported
    const actionButtonsOverlayOpacity = useTransform(scrollYContainerProgress, [0, -1], [0, 1], {
      mixer: (from, to) => v => (v < 1 ? from : to),
    });

    const { scrollYProgress: scrollYTargetProgressOriginal } = useScroll({
      container: ref as RefObject<HTMLDivElement>,
      target: scrollTrackingTargetRef,
      offset: [`${-headerHeight}px start`, `end ${headerHeight}px`],
    });

    // starting to follow scroll after 70% of the way down, mapping from 1 to 0
    const scrollYTargetProgress = useTransform(scrollYTargetProgressOriginal, [0.7, 0], [1, 0]);

    // on android, after the keyboard is closed, the scroll isn't resetting. Manually resetting after 300ms
    const onMobileKeyboardHeightChange = useCallback(
      (isOpen: boolean) => {
        if (isOpen) return;

        setTimeout(() => {
          scrollYTargetProgressOriginal.set(1);
        }, 300);
      },
      [scrollYTargetProgressOriginal],
    );

    useMobileKeyboardHeight({ onChange: onMobileKeyboardHeightChange });

    useEffect(() => {
      // we scroll to 1 on mount so opacity will start at 1 and not 0
      const timeout = setTimeout(() => {
        scrollYTargetProgressOriginal.set(1);
      }, 0);

      return () => clearTimeout(timeout);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStepIndex]);

    const scrollOpacity = useTransform(scrollYTargetProgress, [1, 0.6], [1, 0]);

    const scrollOverlayDisplay = useTransform(scrollYTargetProgress, [1, 0], ['none', 'block'], {
      mixer: (from, to) => v => {
        if (historyLength === 0) return 'none';

        return v < 0.5 ? from : to;
      },
    });

    const frameScrollTWithHistory = useMotionTemplate`calc((1 - var(${hasVisibleHistoryItemVarName})) + (var(${hasVisibleHistoryItemVarName}) * ${scrollYTargetProgress}))`;
    const frameScrollOpacityWithHistory = useMotionTemplate`calc((1 - var(${hasVisibleHistoryItemVarName})) + (var(${hasVisibleHistoryItemVarName}) * ${scrollOpacity}))`;

    useMotionValueEvent(scrollYContainerProgress, 'change', () => {
      requestAnimationFrame(() => {
        if (containerRef?.current == null) return;

        setElementVars(containerRef.current, {
          [styles.frameScrollOpacityVarName]: scrollOpacity.get().toString(),
        });
      });
    });

    // blur active element when scroll target Y is changed
    // useMotionValueEvent(scrollOverlayDisplay, 'change', () => {
    //   (document.activeElement as HTMLElement | undefined)?.blur();
    //   vibrate(50);
    // });

    return (
      <Layout
        as={motion.div}
        layout
        ref={targetRef}
        style={{
          [styles.frameScrollTVarName]: frameScrollTWithHistory,
          [styles.frameScrollOpacityVarName]: frameScrollOpacityWithHistory,
          [styles.frameScrollOverlayDisplayVarName]: scrollOverlayDisplay,
          [styles.frameScrollReachedEndVarName]: actionButtonsOverlayOpacity,
        }}
        width="100%"
      >
        <Flex
          className={clsx(styles.main, {
            [styles.snapContainer]: frameType !== 'page',
          })}
          flexDirection={frameType === 'page' ? 'column' : 'column-reverse'}
          justifyContent="flex-start"
          px={styles.LAYOUT_PADDING_FROM_THE_SIDE}
          ref={ref}
        >
          {children}
        </Flex>
      </Layout>
    );
  },
);
